export type DifficultyLevel =
  | 'Beginner'
  | 'Easy'
  | 'Intermediate'
  | 'Advanced'
  | 'Expert'
  | 'Mixed Difficulty';

export type CanonicalQuizFormat =
  | 'multiple_choice'
  | 'true_false'
  | 'fill_blank'
  | 'short_answer'
  | 'matching'
  | 'open_ended';

export type QuizPurposeMode =
  | 'practice'
  | 'challenge'
  | 'ranked'
  | 'multiplayer'
  | 'live'
  | 'solo'
  | 'daily'
  | 'special';

export interface MatchingPair {
  left: string;
  right: string;
}

export interface ScoreCalculationInput {
  isCorrect: boolean;
  timeTakenMs: number;
  timerSeconds: number;
  timerEnabled: boolean;
  currentStreak: number;
  difficulty?: DifficultyLevel | string;
  questionFormat?: string;
  gameMode?: QuizPurposeMode | string;
  basePoints?: number;
  speedBonusMax?: number;
}

export interface ScoreCalculationResult {
  totalPoints: number;
  baseAwarded: number;
  speedBonus: number;
  streakBonus: number;
  formatMultiplier: number;
  difficultyMultiplier: number;
  newStreak: number;
  countsForRanking: boolean;
}

export function mapToCanonicalFormat(formatLabel: string): CanonicalQuizFormat {
  const lower = (formatLabel || '').trim().toLowerCase();
  if (lower === 'true or false' || lower === 'true_false') {
    return 'true_false';
  }
  if (
    lower === 'fill in the blank' ||
    lower === 'fill_blank' ||
    lower === 'complete the bible verse' ||
    lower === 'missing word challenge'
  ) {
    return 'fill_blank';
  }
  if (lower === 'short answer' || lower === 'short_answer') {
    return 'short_answer';
  }
  if (
    lower === 'matching' ||
    lower === 'match the following' ||
    lower === 'matching pairs' ||
    lower === 'match_following'
  ) {
    return 'matching';
  }
  if (
    lower === 'open-ended reflection' ||
    lower === 'open_ended' ||
    lower === 'open ended' ||
    lower === 'essay'
  ) {
    return 'open_ended';
  }
  return 'multiple_choice';
}

import {
  SCORING_CONFIG,
  calculateQuestionScore,
  calculateSessionXp,
  calculateLevelFromXp as calculateLevelFromXpConfig,
  calculateSessionAccuracy,
  formatLifetimeAccuracy,
  getEvaluationTier,
  formatDisplayNumber,
  compareLeaderboardEntries,
  compareTopAccuracyEntries,
  getLevelThresholdXp,
  getRankTitleForLevel,
  type LevelProgressInfo,
  type EvaluationTierInfo,
  type EvaluationTrophyType,
  type EvaluationTierKey,
} from '../config/scoringConfig.ts';

export {
  SCORING_CONFIG,
  calculateQuestionScore,
  calculateSessionXp,
  calculateSessionAccuracy,
  formatLifetimeAccuracy,
  getEvaluationTier,
  formatDisplayNumber,
  compareLeaderboardEntries,
  compareTopAccuracyEntries,
  getLevelThresholdXp,
  getRankTitleForLevel,
  type LevelProgressInfo,
  type EvaluationTierInfo,
  type EvaluationTrophyType,
  type EvaluationTierKey,
};

export function calculateAuthoritativeScore(
  input: ScoreCalculationInput
): ScoreCalculationResult {
  const modeLower = (input.gameMode || 'challenge').toLowerCase();
  const canonicalFmt = mapToCanonicalFormat(
    input.questionFormat || 'Multiple Choice'
  );
  // Practice mode and Open-Ended format never impact competitive ranked leaderboards
  const countsForRanking =
    modeLower !== 'practice' && canonicalFmt !== 'open_ended';

  const breakdown = calculateQuestionScore({
    isCorrect: Boolean(input.isCorrect),
    timeTakenMs: input.timeTakenMs,
    timerSeconds: input.timerSeconds,
    timerEnabled: canonicalFmt !== 'open_ended' && Boolean(input.timerEnabled),
    currentStreak: input.currentStreak,
  });

  return {
    totalPoints: breakdown.totalPoints,
    baseAwarded: breakdown.basePoints,
    speedBonus: breakdown.speedBonus,
    streakBonus: breakdown.streakBonus,
    formatMultiplier: 1,
    difficultyMultiplier: 1,
    newStreak: breakdown.newStreak,
    countsForRanking,
  };
}

/**
 * Normalizes short biblical answers across English, Filipino, Cebuano, Korean,
 * Simplified Chinese, Traditional Chinese, and Spanish so legitimate answers
 * never fail due to capitalization, extra spaces, or minor formatting differences.
 */
export function normalizeShortAnswer(text: string): string {
  if (!text || typeof text !== 'string') return '';
  return text
    .normalize('NFKC')
    .toLowerCase()
    .replace(/['’"“”.,!?;:()[\]{}\-_/\\]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(
      /^(?:(?:the|a|an|ang|si|sa|ni|king|queen|prophet|apostle|mount|mt|book of|gospel of|hari|propeta|apostol|bundok|bukid sa|bukid|aklat ng|basahon sa)\s+)+/i,
      ''
    )
    .replace(/\s+/g, ' ')
    .trim();
}

const TRUE_SYNONYMS = new Set([
  'true',
  't',
  'yes',
  'tama',
  'tama (true)',
  'tinuod',
  'tinuod (true)',
  '참',
  '참 (true)',
  '正确',
  '正确 (true)',
  '正確',
  '正確 (true)',
  'verdadero',
]);

const FALSE_SYNONYMS = new Set([
  'false',
  'f',
  'no',
  'mali',
  'mali (false)',
  'bakak',
  'bakak (false)',
  '거짓',
  '거짓 (false)',
  '错误',
  '错误 (false)',
  '錯誤',
  '錯誤 (false)',
  'falso',
]);

export function serializeMatchingPairs(pairs: MatchingPair[]): string {
  return [...pairs]
    .sort((a, b) => a.left.localeCompare(b.left))
    .map((p) => `${p.left.trim().toLowerCase()}=>${p.right.trim().toLowerCase()}`)
    .join('||');
}

export interface EvaluateAnswerInput {
  questionFormat: string;
  submittedAnswer: string;
  correctAnswer: string;
  acceptedAnswers?: string[];
  matchingPairs?: MatchingPair[];
  gameMode?: string;
}

export function evaluateQuizAnswer(input: EvaluateAnswerInput): boolean {
  const fmt = mapToCanonicalFormat(input.questionFormat);
  const rawSubmitted = (input.submittedAnswer || '').trim();
  if (!rawSubmitted || rawSubmitted === '__TIMEOUT__') {
    return false;
  }

  // Open-Ended Reflection is only for Practice / Educational self-assessment
  if (fmt === 'open_ended') {
    const mode = (input.gameMode || 'practice').toLowerCase();
    if (mode === 'ranked' || mode === 'multiplayer') {
      return false;
    }
    return rawSubmitted.length >= 3;
  }

  // Matching format evaluation
  if (fmt === 'matching' && input.matchingPairs && input.matchingPairs.length > 0) {
    try {
      const parsed = JSON.parse(rawSubmitted);
      if (Array.isArray(parsed)) {
        const expectedSerialized = serializeMatchingPairs(input.matchingPairs);
        const submittedSerialized = serializeMatchingPairs(parsed);
        return expectedSerialized === submittedSerialized;
      } else if (typeof parsed === 'object' && parsed !== null) {
        const pairsFromObject: MatchingPair[] = Object.entries(parsed).map(
          ([left, right]) => ({
            left: String(left),
            right: String(right),
          })
        );
        const expectedSerialized = serializeMatchingPairs(input.matchingPairs);
        const submittedSerialized = serializeMatchingPairs(pairsFromObject);
        return expectedSerialized === submittedSerialized;
      }
    } catch {
      // Fallback string comparison if not JSON
    }
    const normSub = normalizeShortAnswer(rawSubmitted);
    const normExpected = normalizeShortAnswer(input.correctAnswer);
    return normSub === normExpected;
  }

  // True or False evaluation across languages
  if (fmt === 'true_false') {
    const subLower = rawSubmitted.toLowerCase();
    const expLower = (input.correctAnswer || '').trim().toLowerCase();
    if (subLower === expLower) return true;
    const isExpTrue = TRUE_SYNONYMS.has(expLower);
    const isExpFalse = FALSE_SYNONYMS.has(expLower);
    if (isExpTrue && TRUE_SYNONYMS.has(subLower)) return true;
    if (isExpFalse && FALSE_SYNONYMS.has(subLower)) return true;
  }

  // Direct case-insensitive match
  if (
    rawSubmitted.toLowerCase() ===
    (input.correctAnswer || '').trim().toLowerCase()
  ) {
    return true;
  }

  // Normalized short answer / fill-in-the-blank comparison
  const normSub = normalizeShortAnswer(rawSubmitted);
  const normCorrect = normalizeShortAnswer(input.correctAnswer);
  if (normSub && normSub === normCorrect) {
    return true;
  }

  if (Array.isArray(input.acceptedAnswers)) {
    for (const acc of input.acceptedAnswers) {
      if (!acc) continue;
      if (rawSubmitted.toLowerCase() === acc.trim().toLowerCase()) {
        return true;
      }
      const normAcc = normalizeShortAnswer(acc);
      if (normSub && normSub === normAcc) {
        return true;
      }
    }
  }

  return false;
}

export function shuffleOptions<T>(items: T[], seed?: number): T[] {
  const arr = [...items];
  let s = seed ?? Math.floor(Math.random() * 2147483647);
  for (let i = arr.length - 1; i > 0; i--) {
    s = (s * 1664525 + 1013904223) % 4294967296;
    const j = Math.floor((s / 4294967296) * (i + 1));
    const temp = arr[i];
    arr[i] = arr[j];
    arr[j] = temp;
  }
  return arr;
}

export function normalizeQuestionText(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(
      /\b(the|a|an|in|of|to|and|who|what|which|where|when|how|is|was|did|for)\b/g,
      ' '
    )
    .replace(/\s+/g, ' ')
    .trim();
}

export function computeSemanticSimilarity(a: string, b: string): number {
  const normA = normalizeQuestionText(a);
  const normB = normalizeQuestionText(b);
  if (!normA || !normB) return 0;
  if (normA === normB) return 1;

  const tokensA = new Set(normA.split(' ').filter((w) => w.length > 2));
  const tokensB = new Set(normB.split(' ').filter((w) => w.length > 2));

  if (tokensA.size === 0 || tokensB.size === 0) return 0;

  let intersection = 0;
  for (const t of tokensA) {
    if (tokensB.has(t)) {
      intersection++;
    }
  }
  const union = tokensA.size + tokensB.size - intersection;
  return union === 0 ? 0 : intersection / union;
}

export interface CandidateQuestionCheck {
  questionText: string;
  scriptureReference: string;
  correctAnswer: string;
}

export function isDuplicateOrTooSimilar(
  candidate: CandidateQuestionCheck,
  existingQuestions: CandidateQuestionCheck[],
  similarityThreshold = 0.68
): { isDuplicate: boolean; reason?: string } {
  const normCandidate = normalizeQuestionText(candidate.questionText);

  for (const existing of existingQuestions) {
    const normExisting = normalizeQuestionText(existing.questionText);
    if (normCandidate === normExisting) {
      return { isDuplicate: true, reason: 'Exact normalized text match' };
    }

    const sim = computeSemanticSimilarity(
      candidate.questionText,
      existing.questionText
    );
    if (sim >= similarityThreshold) {
      return {
        isDuplicate: true,
        reason: `High semantic similarity (${Math.round(sim * 100)}%)`,
      };
    }

    if (
      candidate.scriptureReference.trim().toLowerCase() ===
        existing.scriptureReference.trim().toLowerCase() &&
      candidate.correctAnswer.trim().toLowerCase() ===
        existing.correctAnswer.trim().toLowerCase() &&
      sim >= 0.45
    ) {
      return {
        isDuplicate: true,
        reason: 'Same scripture reference and identical answer concept',
      };
    }
  }

  return { isDuplicate: false };
}

const CANONICAL_BIBLE_BOOKS = new Set([
  'genesis', 'exodus', 'leviticus', 'numbers', 'deuteronomy',
  'joshua', 'judges', 'ruth', '1 samuel', '2 samuel', '1 kings', '2 kings',
  '1 chronicles', '2 chronicles', 'ezra', 'nehemiah', 'esther', 'job',
  'psalms', 'psalm', 'proverbs', 'ecclesiastes', 'song of solomon', 'song of songs',
  'isaiah', 'jeremiah', 'lamentations', 'ezekiel', 'daniel',
  'hosea', 'joel', 'amos', 'obadiah', 'jonah', 'micah', 'nahum',
  'habakkuk', 'zephaniah', 'haggai', 'zechariah', 'malachi',
  'matthew', 'mark', 'luke', 'john', 'acts', 'romans',
  '1 corinthians', '2 corinthians', 'galatians', 'ephesians', 'philippians',
  'colossians', '1 thessalonians', '2 thessalonians', '1 timothy', '2 timothy',
  'titus', 'philemon', 'hebrews', 'james', '1 peter', '2 peter',
  '1 john', '2 john', '3 john', 'jude', 'revelation'
]);

export function validateBiblicalReference(reference: string): {
  isValid: boolean;
  book: string;
  chapter: number;
  verseStart: number;
} {
  if (!reference || typeof reference !== 'string') {
    return { isValid: false, book: '', chapter: 0, verseStart: 0 };
  }
  const cleaned = reference.trim();
  const match = cleaned.match(/^((?:[1-3]\s+)?[A-Za-z\s]+?)\s+(\d+)(?::(\d+))?/);
  if (!match) {
    return { isValid: false, book: '', chapter: 0, verseStart: 0 };
  }
  const bookRaw = match[1].trim();
  const bookLower = bookRaw.toLowerCase();
  const chapter = parseInt(match[2], 10);
  const verseStart = match[3] ? parseInt(match[3], 10) : 1;

  if (!CANONICAL_BIBLE_BOOKS.has(bookLower)) {
    return { isValid: false, book: bookRaw, chapter, verseStart };
  }
  if (isNaN(chapter) || chapter < 1 || chapter > 150) {
    return { isValid: false, book: bookRaw, chapter: 0, verseStart: 0 };
  }
  return {
    isValid: true,
    book: bookRaw,
    chapter,
    verseStart: isNaN(verseStart) || verseStart < 1 ? 1 : verseStart,
  };
}

export function calculateLevelFromXp(xp: number): LevelProgressInfo {
  return calculateLevelFromXpConfig(xp);
}

export function evaluateUnlockedAchievements(stats: {
  totalGames: number;
  accuracy: number;
  maxStreak: number;
  multiplayerWins: number;
  oldTestamentCorrect: number;
  newTestamentCorrect: number;
  totalQuestionsAnswered: number;
  dailyStreakDays: number;
}): string[] {
  const unlocked: string[] = [];
  if (stats.totalGames >= 1) unlocked.push('FIRST_VICTORY');
  if (stats.accuracy === 100 && stats.totalGames >= 1)
    unlocked.push('PERFECT_SCORE');
  if (stats.totalQuestionsAnswered >= 25) unlocked.push('BIBLE_SCHOLAR');
  if (stats.totalQuestionsAnswered >= 100) unlocked.push('SCRIPTURE_MASTER');
  if (stats.dailyStreakDays >= 7) unlocked.push('SEVEN_DAY_STREAK');
  if (stats.maxStreak >= 10) unlocked.push('TEN_STREAK_MASTER');
  if (stats.multiplayerWins >= 1) unlocked.push('MULTIPLAYER_CHAMPION');
  if (stats.oldTestamentCorrect >= 15) unlocked.push('OLD_TESTAMENT_EXPERT');
  if (stats.newTestamentCorrect >= 15) unlocked.push('NEW_TESTAMENT_EXPERT');
  if (stats.totalGames >= 10) unlocked.push('BIBLE_MARATHON');
  return unlocked;
}
